// Every status the system knows, and the words the user sees for it.
// Chip forms follow the design brief §6.

export const ROLES = ['admin', 'broker', 'evaluator', 'user'] as const;
export type Role = (typeof ROLES)[number];

export const BROKER_REQUEST_STATUSES = [
  'new',
  'assigned',
  'active',
  'contract_signed',
  'licensed',
  'offer_pending',
  'completed',
  'cancelled',
] as const;
export type BrokerRequestStatus = (typeof BROKER_REQUEST_STATUSES)[number];

/** Statuses that occupy one of the broker's membership slots. */
export const SLOT_OCCUPYING_STATUSES: readonly BrokerRequestStatus[] = [
  'assigned',
  'active',
  'contract_signed',
  'licensed',
  'offer_pending',
];

/** The owner may cancel from the app only while the request is in one of these. */
export const OWNER_CANCELLABLE_STATUSES: readonly BrokerRequestStatus[] = ['new', 'assigned', 'active'];

export const COMMISSION_STATUSES = ['none', 'due', 'paid'] as const;
export type CommissionStatus = (typeof COMMISSION_STATUSES)[number];

export const EVALUATION_STATUSES = [
  'new',
  'assigned',
  'accepted',
  'fee_set',
  'in_progress',
  'report_ready',
  'completed',
  'cancelled',
] as const;
export type EvaluationStatus = (typeof EVALUATION_STATUSES)[number];
export const EVALUATION_SLOT_STATUSES: readonly EvaluationStatus[] = [
  'assigned',
  'accepted',
  'fee_set',
  'in_progress',
  'report_ready',
];

export const VIEWING_STATUSES = [
  'with_broker',
  'sent_to_owner',
  'back_to_buyer',
  'confirmed',
  'done',
  'no_show',
  'declined',
  'cancelled',
] as const;
export type ViewingStatus = (typeof VIEWING_STATUSES)[number];

export const VIEWING_PARTIES = ['buyer', 'broker', 'owner'] as const;
export type ViewingParty = (typeof VIEWING_PARTIES)[number];

export const PAYMENT_KINDS = ['evaluation_fee', 'membership', 'commission'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];
export const PAYMENT_STATUSES = ['pending', 'paid', 'failed', 'refunded'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];
export const PAYMENT_PROVIDERS = ['moyasar', 'bank_transfer', 'mock'] as const;
export type PaymentProvider = (typeof PAYMENT_PROVIDERS)[number];

export const MEMBERSHIP_STATUSES = ['none', 'active', 'past_due', 'cancelled', 'expired'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];
export const MEMBERSHIP_TIER_KEYS = ['silver', 'gold', 'diamond'] as const;
export type MembershipTierKey = (typeof MEMBERSHIP_TIER_KEYS)[number];

export const PROPERTY_PURPOSES = ['sell', 'rent'] as const;
export type PropertyPurpose = (typeof PROPERTY_PURPOSES)[number];
export const PROPERTY_TYPES = [
  'apartment',
  'villa',
  'floor',
  'building',
  'land',
  'rest_house',
  'farm',
  'chalet',
  'shop',
  'office',
  'warehouse',
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];
export const FACADES = ['north', 'south', 'east', 'west', 'corner'] as const;
export type Facade = (typeof FACADES)[number];
export const RENT_PERIODS = ['yearly', 'monthly', 'daily'] as const;
export type RentPeriod = (typeof RENT_PERIODS)[number];
export const TENANT_TYPES = ['families', 'singles'] as const;
export type TenantType = (typeof TENANT_TYPES)[number];
export const LAND_USES = ['residential', 'commercial'] as const;
export type LandUse = (typeof LAND_USES)[number];
export const LISTING_STATUSES = ['draft', 'active', 'paused', 'sold', 'removed'] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

export const REQUEST_KINDS = ['BR', 'EV', 'VE'] as const;
export type RequestKind = (typeof REQUEST_KINDS)[number];

export const EVENT_TYPES = [
  'received',
  'assigned',
  'accepted',
  'declined',
  'expired',
  'reassigned',
  'contract_signed',
  'licensed',
  'listed',
  'note',
  'offer',
  'offer_accepted',
  'offer_rejected',
  'offer_withdrawn',
  'viewing_proposed',
  'viewing_forwarded',
  'viewing_confirmed',
  'viewing_declined',
  'fee_set',
  'paid',
  'payment_failed',
  'report_uploaded',
  'completed',
  'commission_due',
  'commission_confirmed',
  'cancelled',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const SIGNUP_KINDS = ['broker', 'evaluator'] as const;
export type SignupKind = (typeof SIGNUP_KINDS)[number];
export const SIGNUP_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type SignupStatus = (typeof SIGNUP_STATUSES)[number];

export type ChipForm = 'outlined' | 'filled' | 'mint' | 'warning' | 'muted';
export interface StatusLabel { ar: string; en: string; chip: ChipForm }

export const BROKER_REQUEST_LABELS: Record<BrokerRequestStatus, StatusLabel> = {
  new: { ar: 'جديد', en: 'New', chip: 'outlined' },
  assigned: { ar: 'بانتظار قبول الوسيط', en: 'Awaiting broker', chip: 'outlined' },
  active: { ar: 'نشط', en: 'Active', chip: 'outlined' },
  contract_signed: { ar: 'وُقّع العقد', en: 'Contract signed', chip: 'outlined' },
  licensed: { ar: 'مرخّص', en: 'Licensed', chip: 'outlined' },
  offer_pending: { ar: 'عرض جديد', en: 'New offer', chip: 'warning' },
  completed: { ar: 'مكتمل', en: 'Completed', chip: 'filled' },
  cancelled: { ar: 'ملغى', en: 'Cancelled', chip: 'muted' },
};

/** What the broker sees for a completed request depends on the commission. */
export const COMMISSION_LABELS: Record<CommissionStatus, StatusLabel> = {
  none: { ar: '—', en: '—', chip: 'muted' },
  due: { ar: 'بانتظار سداد العمولة', en: 'Needs payment', chip: 'warning' },
  paid: { ar: 'منجز', en: 'Done', chip: 'filled' },
};

export const EVALUATION_LABELS: Record<EvaluationStatus, StatusLabel> = {
  new: { ar: 'جديد', en: 'New', chip: 'outlined' },
  assigned: { ar: 'بانتظار قبول المقيّم', en: 'Awaiting valuer', chip: 'outlined' },
  accepted: { ar: 'قبل المقيّم', en: 'Accepted', chip: 'outlined' },
  fee_set: { ar: 'حُدّدت الرسوم — ادفع الآن', en: 'Fee set — pay now', chip: 'warning' },
  in_progress: { ar: 'قيد التقييم', en: 'In progress', chip: 'outlined' },
  report_ready: { ar: 'التقرير جاهز', en: 'Report ready', chip: 'filled' },
  completed: { ar: 'مكتمل', en: 'Completed', chip: 'filled' },
  cancelled: { ar: 'ملغى', en: 'Cancelled', chip: 'muted' },
};

export const VIEWING_LABELS: Record<ViewingStatus, StatusLabel> = {
  with_broker: { ar: 'لدى الوسيط', en: 'With broker', chip: 'mint' },
  sent_to_owner: { ar: 'أُرسل للمالك', en: 'Sent to owner', chip: 'mint' },
  back_to_buyer: { ar: 'بانتظار ردك', en: 'Awaiting your reply', chip: 'warning' },
  confirmed: { ar: 'مؤكّدة', en: 'Confirmed', chip: 'filled' },
  done: { ar: 'تمت', en: 'Done', chip: 'filled' },
  no_show: { ar: 'لم يحضر', en: 'No-show', chip: 'muted' },
  declined: { ar: 'مرفوضة', en: 'Declined', chip: 'muted' },
  cancelled: { ar: 'ملغاة', en: 'Cancelled', chip: 'muted' },
};

export const PROPERTY_TYPE_LABELS: Record<PropertyType, { ar: string; en: string }> = {
  apartment: { ar: 'شقة', en: 'Apartment' },
  villa: { ar: 'فيلا', en: 'Villa' },
  floor: { ar: 'دور', en: 'Floor' },
  building: { ar: 'عمارة', en: 'Building' },
  land: { ar: 'أرض', en: 'Land' },
  rest_house: { ar: 'استراحة', en: 'Rest house' },
  farm: { ar: 'مزرعة', en: 'Farm' },
  chalet: { ar: 'شاليه', en: 'Chalet' },
  shop: { ar: 'محل تجاري', en: 'Shop' },
  office: { ar: 'مكتب', en: 'Office' },
  warehouse: { ar: 'مستودع', en: 'Warehouse' },
};
